import { Logo } from '@neuroforge/ui'

type ComingSoonApp = 'academy' | 'prompts' | 'forge' | 'madsaas'

type ComingSoonPageProps = {
  app?: ComingSoonApp
  hubUrl?: string
}

type ComingSoonCopy = {
  eyebrow: string
  title: string
  description: string
  accentTextClass: string
  badgeClass: string
  primaryClass: string
  glowClass: string
}

const APP_COPY: Record<ComingSoonApp, ComingSoonCopy> = {
  academy: {
    eyebrow: 'NeuroForge Academy',
    title: 'Bientôt disponible',
    description:
      'Notre plateforme de formation IA est en cours de finalisation. Prépare-toi à forger tes compétences avec des parcours guidés, des cours interactifs et une communauté passionnée.',
    accentTextClass: 'text-cyan-500 dark:text-cyan-300',
    badgeClass: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-500 dark:text-cyan-300',
    primaryClass:
      'bg-gradient-to-r from-cyan-500 to-blue-500 shadow-cyan-500/25 hover:shadow-cyan-500/35 text-white',
    glowClass: 'bg-cyan-500/20',
  },
  prompts: {
    eyebrow: 'NeuroForge Prompts',
    title: 'Bientôt disponible',
    description:
      "La bibliothèque de prompts IA arrive très bientôt. Explore, partage et perfectionne tes prompts avec des templates prêts à l'emploi et une communauté de créateurs.",
    accentTextClass: 'text-violet-500 dark:text-violet-300',
    badgeClass: 'border-violet-500/30 bg-violet-500/10 text-violet-500 dark:text-violet-300',
    primaryClass:
      'bg-gradient-to-r from-violet-500 to-indigo-500 shadow-violet-500/25 hover:shadow-violet-500/35 text-white',
    glowClass: 'bg-violet-500/20',
  },
  forge: {
    eyebrow: 'La Forge by MADSAAS',
    title: 'Bientôt disponible',
    description:
      "L'atelier SaaS privé est en cours de finalisation. Bientôt, tu pourras forger ton produit, ton repo et ta capacité de livraison avec un accompagnement sur mesure.",
    accentTextClass: 'text-amber-500 dark:text-amber-300',
    badgeClass: 'border-amber-500/30 bg-amber-500/10 text-amber-500 dark:text-amber-300',
    primaryClass:
      'bg-gradient-to-r from-amber-500 to-orange-500 shadow-amber-500/25 hover:shadow-amber-500/35 text-white',
    glowClass: 'bg-amber-500/20',
  },
  madsaas: {
    eyebrow: 'MADSAAS',
    title: 'Bientôt disponible',
    description:
      'Le site MADSAAS arrive : développement SaaS expert, studio produit (La Forge) et accompagnement IA. Reviens très vite.',
    accentTextClass: 'text-violet-500 dark:text-violet-300',
    badgeClass: 'border-violet-500/30 bg-violet-500/10 text-violet-500 dark:text-violet-300',
    primaryClass:
      'bg-gradient-to-r from-violet-500 to-cyan-500 shadow-violet-500/25 hover:shadow-violet-500/35 text-white',
    glowClass: 'bg-violet-500/20',
  },
}

export default function ComingSoonPage({ app = 'prompts', hubUrl = '/' }: ComingSoonPageProps) {
  const copy = APP_COPY[app]

  return (
    <main className="text-(--text) relative isolate flex min-h-screen items-center justify-center overflow-hidden bg-[var(--bg)] px-4 py-16">
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute left-1/2 top-1/4 -z-10 h-72 w-72 -translate-x-1/2 rounded-full blur-3xl ${copy.glowClass}`}
      />

      <section className="border-(--border) w-full max-w-2xl rounded-[2rem] border bg-[var(--bg)] p-8 shadow-2xl backdrop-blur-2xl md:p-12">
        <div className="flex flex-col items-center gap-6 text-center">
          <Logo size={80} />

          <p className={`text-xs font-black uppercase tracking-[0.28em] ${copy.accentTextClass}`}>
            {copy.eyebrow}
          </p>

          <div
            className={`inline-flex items-center rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-wider ${copy.badgeClass}`}
          >
            En construction
          </div>

          <h1 className="text-4xl font-black tracking-tight md:text-5xl">{copy.title}</h1>

          <p className="text-(--muted) max-w-lg text-sm leading-7 md:text-base">
            {copy.description}
          </p>

          <a
            href={hubUrl}
            className={`mt-4 inline-flex items-center justify-center rounded-2xl px-6 py-3 text-sm font-extrabold shadow-lg transition-all hover:-translate-y-0.5 ${copy.primaryClass}`}
          >
            Retour au Hub
          </a>
        </div>
      </section>
    </main>
  )
}
